import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../../../shared/components/card/Card";
import {
  catchFunction,
  formatToEurosCurrency,
} from "../../../../../../shared/lib/utils";
import ObjectiveTrackerCollapseButton from "./ObjectiveTrackerCollapseButton";
import { useState } from "react";
import useAxios from "../../../../../../hooks/useAxios";
import useLoading from "../../../../../../shared/components/loading/useLoading";
import {
  OBJECTIVE_TRACKER_DUPLICATE_PERIOD,
  OBJECTIVE_TRACKER_PERIOD_BY_ID,
  OBJECTIVE_TRACKER_SUBGOALS,
} from "../../../../../../shared/lib/apiEndpointsResolver";
import ObjectivesTrackerSubgoal from "./ObjectivesTrackerSubgoal";
import CreateSubgoalDialog from "../create-subgoal/CreateSubgoalDialog";
import useToastWrapper from "../../../../../../hooks/useToastWrapper";
import useConfirmDialog from "../../../../../../shared/components/confirm-dialog/useConfirmDialog";
import EditPeriodDialog from "../edit-period/EditPeriodDialog";
import ObjectivesTrackerDuplicateButton from "./ObjectivesTrackerDuplicateButton";
import ObjectivesTrackerArrowIndicator from "./ObjectivesTrackerArrowIndicator";
import ObjectivesTrackerDeleteButton from "./ObjectivesTrackerDeleteButton";

const ObjectivesTrackerPeriod = ({
  index,
  periodNumber,
  trackerId,
  trackerGoal,
  period,
  selectables,
  removePeriodFromParent,
  updatePeriodFromParent,
  addPeriodToParent,
}) => {
  const { t } = useTranslation();
  const axios = useAxios();
  const { setLoading } = useLoading();
  const [subgoals, setSubgoals] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const toast = useToastWrapper();
  const deletePeriodDialog = useConfirmDialog();

  const loadPeriodSubgoals = () => {
    if (!isOpen) {
      if (subgoals.length === 0) {
        setLoading(true);
        axios
          .get(OBJECTIVE_TRACKER_SUBGOALS(trackerId, period.id))
          .then((response) => {
            setSubgoals(response.data);
          })
          .catch(catchFunction)
          .finally(() => {
            setLoading(false);
            setIsOpen(true);
          });
      } else {
        setIsOpen(true);
      }
    } else {
      setIsOpen(false);
    }
  };

  const onSubgoalCreation = (formValues) => {
    setLoading(true);
    const request = {
      parentSubgoalId: null,
      ratio: +formValues.ratio * 0.01,
      dimension: {
        id: formValues.dimensionId,
        name: formValues.dimensionName,
        label: formValues.dimensionLabel,
        value: formValues.dimensionValue,
      },
    };
    axios
      .post(OBJECTIVE_TRACKER_SUBGOALS(trackerId, period?.id), request)
      .then((response) => {
        setSubgoals([...subgoals, response.data]);
        toast.success(
          t("objectivesTracker.createSubgoalForm.success", {
            name: `${response.data.dimension.label}: ${response.data.dimension.value}`,
          }),
        );
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const removeSubgoal = (subgoalId) => {
    const subgoalsCopy = [...subgoals];
    const result = subgoalsCopy.filter((s) => s.id !== subgoalId);
    setSubgoals(result);
  };

  const onPeriodDeletionClickHandler = () => {
    deletePeriodDialog.display(
      t("objectivesTracker.deletePeriodConfirmation", {
        from: period.beginDate,
        to: period.endDate,
      }),
    );
  };

  const onPeriodDeletion = () => {
    setLoading(true);
    axios
      .delete(OBJECTIVE_TRACKER_PERIOD_BY_ID(trackerId, period.id))
      .then((_) => {
        removePeriodFromParent(period.id);
        toast.success(
          t("objectivesTracker.deletePeriod", {
            from: period.beginDate,
            to: period.endDate,
          }),
        );
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const onPeriodEdition = (formValues) => {
    setLoading(true);
    const request = {
      dateRange: {
        from: formValues.beginDate,
        to: formValues.endDate,
      },
      goal: +formValues.goal,
    };
    axios
      .put(OBJECTIVE_TRACKER_PERIOD_BY_ID(trackerId, period.id), request)
      .then((response) => {
        updatePeriodFromParent(response.data);
        setIsOpen(false);
        setSubgoals([]);
        toast.success(
          t("objectivesTracker.editPeriodForm.success", {
            from: response.data.beginDate,
            to: response.data.endDate,
          }),
        );
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const onPeriodDuplication = () => {
    setLoading(true);
    axios
      .post(OBJECTIVE_TRACKER_DUPLICATE_PERIOD(trackerId, period.id))
      .then((response) => {
        addPeriodToParent(response.data);
        toast.success(
          t("objectivesTracker.duplicatePeriod", {
            from: response.data.beginDate,
            to: response.data.endDate,
          }),
        );
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const addSubgoal = (subgoal) => {
    setSubgoals([...subgoals, subgoal]);
  };

  const updateSubgoal = (subgoal) => {
    const subgoalsCopy = [...subgoals];
    const index = subgoals.findIndex((s) => s.id === subgoal.id);
    subgoalsCopy[index] = subgoal;
    setSubgoals(subgoalsCopy);
  };

  return (
    <>
      {deletePeriodDialog.renderSafeDialog(onPeriodDeletion)}
      <Card
        className={period.current ? "my-1 border-adq-blue/50 border-2" : "my-1"}
      >
        <CardHeader className="pl-3 pt-3 pb-0 pr-0">
          <CardTitle>
            {t("objectivesTracker.detail.periodLabel")} {periodNumber + 1}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-1">
          <div className="flex flex-row justify-between items-center">
            <div className="w-1/12">
              <ObjectiveTrackerCollapseButton
                isDisabled={isDisabled}
                onClickHandler={loadPeriodSubgoals}
              />
            </div>
            <div className="flex flex-col ml-4">
              <div>
                {t("objectivesTracker.detail.beginDateLabel")}:{" "}
                {period?.beginDate}
              </div>
              <div>
                {t("objectivesTracker.detail.endDateLabel")}: {period?.endDate}
              </div>
            </div>
            <div>
              {t("objectivesTracker.detail.objectiveLabel")}:{" "}
              {formatToEurosCurrency(period?.result?.goal)}
            </div>
            <div>
              {t("objectivesTracker.detail.achievedLabel")} :{" "}
              {formatToEurosCurrency(period?.result?.achieved)}
            </div>
            <div>
              {t("objectivesTracker.detail.expectedLabel")} :{" "}
              {formatToEurosCurrency(period?.result?.expected)}
            </div>
            <div className="flex flex-row gap-x-1">
              {t("objectivesTracker.detail.differenceLabel")}:{" "}
              {formatToEurosCurrency(period?.result?.difference)}
              <ObjectivesTrackerArrowIndicator
                difference={period?.result?.difference}
              />
            </div>
            {period?.current ? (
              <div>
                {t("objectivesTracker.detail.requiredDailySpendLabel")} :{" "}
                {formatToEurosCurrency(period?.result?.requiredDailySpend)}
              </div>
            ) : (
              <></>
            )}
            <div className="flex flex-row gap-x-1">
              <CreateSubgoalDialog
                onSubmitHandler={onSubgoalCreation}
                parentGoal={period?.result?.goal}
                selectables={selectables}
              />
              <ObjectivesTrackerDuplicateButton
                onClickHandler={onPeriodDuplication}
              />
              <EditPeriodDialog
                onSubmitHandler={onPeriodEdition}
                trackerGoal={trackerGoal}
                period={period}
              />
              <ObjectivesTrackerDeleteButton
                onDeleteHandler={onPeriodDeletionClickHandler}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      {isOpen &&
        subgoals &&
        subgoals.map((subgoal) => (
          <ObjectivesTrackerSubgoal
            key={subgoal.id}
            level={index + 1}
            isCurrent={period?.current}
            trackerId={trackerId}
            periodId={period?.id}
            subgoal={subgoal}
            selectables={selectables}
            removeSubgoalFromParent={removeSubgoal}
            updateSubgoalFromParent={updateSubgoal}
            addSubgoalToParent={addSubgoal}
          />
        ))}
    </>
  );
};

export default ObjectivesTrackerPeriod;
